import { GlobalOrdersRequest } from '@api/ringil-api.v2.tsx';
import { ExpeditionLocality, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { isNotBlank } from '@utils/utils.tsx';

/**
 * What makes a route valid, and how to report the ways it is not.
 *
 * Every order has to be loaded exactly once, unloaded exactly once, and loaded before it is
 * unloaded. Editing the route deliberately does *not* enforce this — stops and orders move where the
 * user puts them, even into a broken arrangement — so the rules live here as a check rather than as
 * a repair, and the result is reported per order until it is sorted out.
 */

export enum RouteOrderIssueType {
  /** Something is marked for unloading that is not loaded anywhere. */
  NOT_LOADED = 'NOT_LOADED',
  LOADED_MULTIPLE_TIMES = 'LOADED_MULTIPLE_TIMES',
  NOT_UNLOADED = 'NOT_UNLOADED',
  UNLOADED_MULTIPLE_TIMES = 'UNLOADED_MULTIPLE_TIMES',
  UNLOADED_BEFORE_LOADING = 'UNLOADED_BEFORE_LOADING',
}

export interface RouteOrderIssue {
  orderId: string;
  /** Absent for an assignment pointing at an order that no longer exists. */
  orderName?: string;
  type: RouteOrderIssueType;
}

/** Stops a given order may be loaded at: anywhere except the last stop. */
export const loadingCandidateIndexes = (locations: ExpeditionLocality[], currentLocIndex: number): number[] =>
  locations.map((_, index) => index).filter(index => index !== currentLocIndex && index !== locations.length - 1);

/** Stops a given order may be unloaded at: anywhere after the one it is loaded at. */
export const unloadingCandidateIndexes = (locations: ExpeditionLocality[], loadedAtIndex: number, currentLocIndex: number): number[] =>
  locations.map((_, index) => index).filter(index => index !== currentLocIndex && index > loadedAtIndex);

/** Orders loaded at some other stop, i.e. the ones this stop could take over the loading of. */
export const loadableOrdersFrom = (locations: ExpeditionLocality[], locIndex: number): GlobalOrdersRequest[] =>
  locations
    .filter((_, index) => index !== locIndex)
    .flatMap(loc => loc.orders || [])
    .filter(order => order != null)
    .filter(order => isNotBlank(order.orderName));

export const findLoadingIndex = (locations: ExpeditionLocality[], orderId: string): number => locations.findIndex(loc => loc.orders?.some(order => order.id === orderId));

/** Every way the current route breaks the loading/unloading rules, one entry per order and reason. */
export const validateRouteAssignments = (values: NewExpeditionForm): RouteOrderIssue[] => {
  const locations = values?.locations ?? [];
  if (locations.length === 0) return [];

  const loadedAt = new Map<string, number[]>();
  const orderNameById = new Map<string, string>();
  locations.forEach((loc, index) =>
    loc.orders?.forEach(order => {
      if (!order?.id) return;
      loadedAt.set(order.id, [...(loadedAt.get(order.id) ?? []), index]);
      orderNameById.set(order.id, order.orderName);
    }),
  );

  const unloadedAt = new Map<string, number[]>();
  locations.forEach((loc, index) =>
    loc.unloadingOrderIds?.forEach(orderId => {
      unloadedAt.set(orderId, [...(unloadedAt.get(orderId) ?? []), index]);
    }),
  );

  const issues: RouteOrderIssue[] = [];
  const add = (orderId: string, type: RouteOrderIssueType) => issues.push({ orderId, orderName: orderNameById.get(orderId), type });

  loadedAt.forEach((loadIndexes, orderId) => {
    if (loadIndexes.length > 1) add(orderId, RouteOrderIssueType.LOADED_MULTIPLE_TIMES);

    const unloadIndexes = unloadedAt.get(orderId) ?? [];
    if (unloadIndexes.length === 0) {
      add(orderId, RouteOrderIssueType.NOT_UNLOADED);
      return;
    }
    if (unloadIndexes.length > 1) add(orderId, RouteOrderIssueType.UNLOADED_MULTIPLE_TIMES);

    // Measured against the earliest loading stop: unloading before even that cannot be right.
    const firstLoadIndex = Math.min(...loadIndexes);
    if (unloadIndexes.some(unloadIndex => unloadIndex <= firstLoadIndex)) add(orderId, RouteOrderIssueType.UNLOADED_BEFORE_LOADING);
  });

  unloadedAt.forEach((_, orderId) => {
    if (!loadedAt.has(orderId)) add(orderId, RouteOrderIssueType.NOT_LOADED);
  });

  return issues;
};

/** The issues affecting one order, for marking it where it is shown. */
export const issuesForOrder = (issues: RouteOrderIssue[], orderId: string): RouteOrderIssue[] => issues.filter(issue => issue.orderId === orderId);

type Translate = (key: string, defaultText?: string, data?: any) => string;

/** One route issue as a sentence, including what to do about it. */
export const describeRouteIssue = (issue: RouteOrderIssue, tr: Translate): string => {
  const order = issue.orderName || tr('CreateExpedition.routeIssueUnnamedOrder', 'Unnamed order');

  switch (issue.type) {
    case RouteOrderIssueType.NOT_UNLOADED:
      return tr(
        'CreateExpedition.routeIssueNotUnloaded',
        'Order {{order}} is loaded but never unloaded. Add it to the unloading list of a stop that comes after the one it is loaded at.',
        { order },
      );
    case RouteOrderIssueType.UNLOADED_MULTIPLE_TIMES:
      return tr(
        'CreateExpedition.routeIssueUnloadedMultipleTimes',
        'Order {{order}} is unloaded at more than one stop. An order can only be delivered once — leave it in a single unloading list.',
        { order },
      );
    case RouteOrderIssueType.UNLOADED_BEFORE_LOADING:
      return tr(
        'CreateExpedition.routeIssueUnloadedBeforeLoading',
        'Order {{order}} is unloaded at a stop that comes before the one it is loaded at. Move the unloading to a later stop, or load the order earlier in the route.',
        { order },
      );
    case RouteOrderIssueType.LOADED_MULTIPLE_TIMES:
      return tr(
        'CreateExpedition.routeIssueLoadedMultipleTimes',
        'Order {{order}} is loaded at more than one stop. An order can only be picked up once — keep it at a single loading stop.',
        { order },
      );
    case RouteOrderIssueType.NOT_LOADED:
      return tr(
        'CreateExpedition.routeIssueNotLoaded',
        'An order marked for unloading is not loaded anywhere on the route. Remove it from the unloading list, or load it at an earlier stop.',
      );
  }
};
